import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { BOOK, LAST_CHECKED, STATUS_LABELS, UPDATES } from "@/data/book-updates";

// The address of this page is printed in the book. Do not change the route.
const STATUS_STYLE = {
  "In force": "bg-ink text-paper",
  "Fixed commencement": "bg-pink text-ink",
  "Government intention": "border-2 border-pink text-ink",
  "Consultation only": "border-2 border-ink/30 text-ink/70",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-block whitespace-nowrap px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] ${STATUS_STYLE[status] || "border border-ink/30"}`}>
      {status}
    </span>
  );
}

export default function BookUpdates() {
  const origin = window.location.origin;
  const pageUrl = `${origin}/book/updates`;
  return (
    <main data-testid="book-updates">
      <Seo
        title="Employing People: law updates for this edition | Electra HR"
        siteName="Electra HR"
        description="Changes to UK employment law since 25 October 2026 for readers of Employing People: What HR Won't Tell You (Electra HR edition), newest first, with the chapters affected."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Employing People: law updates for this edition",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              dateModified: LAST_CHECKED.iso,
              about: { "@type": "Book", name: BOOK.title, author: { "@type": "Person", name: BOOK.author } },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "Book law updates", item: pageUrl },
              ],
            },
          ],
        }}
      />

      <section className="grain relative overflow-hidden bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">For readers of the book</p>
          <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight">
            Law updates <span className="text-pink">for this edition.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/70">
            <em>{BOOK.title}</em> by {BOOK.author}. {BOOK.edition}. The law in the book is stated as at{" "}
            {BOOK.lawStatedAt}. Changes since then, and changes already announced, are listed below, newest first.
          </p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
            Law stated as at {BOOK.lawStatedAt} · Page last checked {LAST_CHECKED.label}
          </p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x">
          {/* Desktop: table */}
          <div className="hidden md:block border-2 border-ink bg-white">
            <table className="w-full text-left text-sm" data-testid="book-updates-table">
              <thead className="border-b-2 border-ink font-mono text-[11px] uppercase tracking-[0.14em] text-ink/60">
                <tr>
                  <th className="px-5 py-4 font-normal">Date of change</th>
                  <th className="px-5 py-4 font-normal">What changed</th>
                  <th className="px-5 py-4 font-normal">Status</th>
                  <th className="px-5 py-4 font-normal">Verified</th>
                  <th className="px-5 py-4 font-normal">Chapters</th>
                </tr>
              </thead>
              <tbody>
                {UPDATES.map((u) => (
                  <tr key={u.change} className="border-b border-ink/10 last:border-0 align-top">
                    <td className="whitespace-nowrap px-5 py-5 font-semibold">{u.date}</td>
                    <td className="px-5 py-5 leading-relaxed text-ink/80">{u.change}</td>
                    <td className="px-5 py-5"><StatusBadge status={u.status} /></td>
                    <td className="whitespace-nowrap px-5 py-5 text-ink/60">{u.verified}</td>
                    <td className="px-5 py-5 text-ink/60">{u.chapters}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <ul className="grid gap-4 md:hidden">
            {UPDATES.map((u) => (
              <li key={u.change} className="border-2 border-ink bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display text-lg font-semibold">{u.date}</p>
                  <StatusBadge status={u.status} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">{u.change}</p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.12em] text-ink/50">
                  Chapters {u.chapters} · Verified {u.verified}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">What the status labels mean</h2>
              <dl className="mt-5 space-y-4 text-sm leading-relaxed">
                {STATUS_LABELS.map((s) => (
                  <div key={s.label}>
                    <dt><StatusBadge status={s.label} /></dt>
                    <dd className="mt-2 text-ink/70">{s.text}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-5 text-sm leading-relaxed text-ink/70">
                Check GOV.UK before relying on anything not marked In force.
              </p>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-pink bg-pink/10 p-6 text-sm leading-relaxed text-ink/80">
                This page is general information about the employment law of Great Britain, not legal advice. For
                advice on your own situation, speak to a qualified solicitor.
              </div>
              <div className="border-2 border-ink bg-white p-6">
                <p className="text-base leading-relaxed text-ink/80">
                  Not sure what a change means for your team? Electra HR gives small employers a same-day answer at a
                  fixed fee.
                </p>
                <div className="mt-5 flex flex-wrap gap-5 font-mono text-xs uppercase tracking-[0.18em]">
                  <Link to="/contact" className="inline-flex items-center gap-2 text-ink hover:text-pink">
                    Talk to Electra HR <ArrowRight size={14} />
                  </Link>
                  <Link to="/calculators" className="inline-flex items-center gap-2 text-ink/60 hover:text-pink">
                    Free calculators <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
